import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import { getApiUrl } from '../../config/api';
import MessageThreadModal from '../Calendar/AppSendText/MessageThreadModal';
import '../styles/SMSResponsesSidebar.css';
import { isLucieVUser } from '../../utils/userUtils';

const DISMISSED_KEY = 'sms-dismissed-ids';

const getDismissed = () => {
  try { return new Set(JSON.parse(localStorage.getItem(DISMISSED_KEY) || '[]')); }
  catch { return new Set(); }
};

const saveDismissed = (ids) => {
  localStorage.setItem(DISMISSED_KEY, JSON.stringify([...ids].slice(-500)));
};

const relTime = (dateStr) => {
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const decodeMsg = (s) => {
  if (!s) return '';
  try { return decodeURIComponent(s.replace(/\+/g, ' ')); }
  catch { return s.replace(/\+/g, ' '); }
};

const initials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return parts.length === 1
    ? parts[0].slice(0, 2).toUpperCase()
    : (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const SMSResponsesSidebar = ({ user }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newIds, setNewIds] = useState(new Set());
  const [unreadCount, setUnreadCount] = useState(0);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [threadModal, setThreadModal] = useState(null);
  const seenRef = useRef(new Set());
  const intervalRef = useRef(null);
  const isOpenRef = useRef(false);
  const isFirstFetchRef = useRef(true);
  const isFetchingRef = useRef(false);

  useEffect(() => { isOpenRef.current = isOpen; }, [isOpen]);

  const fetchMessages = useCallback(async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    try {
      const res = await fetch(getApiUrl('/api/appointments/recent-incoming-messages'));
      if (!res.ok) return;
      const data = await res.json();
      setLastRefresh(new Date());

      const dismissed = getDismissed();
      const visible = data.filter(m => !dismissed.has(String(m.ID)));

      const newOnes = visible.filter(m => !seenRef.current.has(m.ID));
      newOnes.forEach(m => seenRef.current.add(m.ID));

      const isFirstFetch = isFirstFetchRef.current;
      isFirstFetchRef.current = false;

      if (newOnes.length > 0 && !isFirstFetch) {
        if (!isOpenRef.current) {
          setUnreadCount(prev => prev + newOnes.length);
          setNewIds(prev => {
            const next = new Set(prev);
            newOnes.forEach(m => next.add(m.ID));
            return next;
          });
        }

        newOnes.forEach(m => {
          const name = m.ClientName || m.PhoneNumber || 'Unknown';
          const body = decodeMsg(m.Message).slice(0, 80);
          toast.success(`New message from ${name}: ${body}`, {
            autoClose: 5000,
            onClick: () => {
              setIsOpen(true);
              setUnreadCount(0);
              setNewIds(new Set());
              setThreadModal({
                phoneNumber: m.PhoneNumber,
                customerName: name,
                appointmentId: m.AppointmentID,
              });
            },
          });
        });
      }

      setMessages(visible);
    } catch (err) {
      console.error('SMS sidebar fetch error:', err);
    } finally {
      isFetchingRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!user || !isLucieVUser(user)) return;
    fetchMessages();
    // 60s poll (single-flighted via isFetchingRef). This endpoint is hit
    // continuously across every open session; keep the cadence conservative.
    intervalRef.current = setInterval(fetchMessages, 60000);
    return () => clearInterval(intervalRef.current);
  }, [user, fetchMessages]);

  const dismiss = (e, id) => {
    e.stopPropagation();
    const next = getDismissed();
    next.add(String(id));
    saveDismissed(next);
    setMessages(prev => prev.filter(m => m.ID !== id));
    setNewIds(prev => { const s = new Set(prev); s.delete(id); return s; });
  };

  const dismissAll = () => {
    const next = getDismissed();
    messages.forEach(m => next.add(String(m.ID)));
    saveDismissed(next);
    setMessages([]);
    setNewIds(new Set());
    setUnreadCount(0);
  };

  const handleToggle = () => {
    setIsOpen(o => {
      if (!o) {
        setUnreadCount(0);
        setNewIds(new Set());
      }
      return !o;
    });
  };

  const openThread = (msg) => {
    const name = msg.ClientName || msg.PhoneNumber || 'Unknown';
    setThreadModal({ phoneNumber: msg.PhoneNumber, customerName: name, appointmentId: msg.AppointmentID });
  };

  if (!isLucieVUser(user)) return null;

  return (
    <>
      <button
        className={`sms-sidebar-toggle ${unreadCount > 0 ? 'has-unread' : ''}`}
        onClick={handleToggle}
        title="SMS Inbox"
      >
        <i className="fas fa-comment-dots" />
        {unreadCount > 0 && <span className="unread-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>}
      </button>

      <div className={`sms-responses-sidebar ${isOpen ? 'open' : ''}`}>
        <div className="sms-sb-header">
          <div className="sms-sb-title">
            <i className="fas fa-inbox" />
            <span>SMS Inbox</span>
            {messages.length > 0 && <span className="sms-sb-pill">{messages.length}</span>}
          </div>
          <div className="sms-sb-actions">
            <button className="sms-sb-btn" onClick={fetchMessages} title="Refresh">
              <i className="fas fa-sync-alt" />
            </button>
            {messages.length > 0 && (
              <button className="sms-sb-btn" onClick={dismissAll} title="Clear all">
                <i className="fas fa-check-double" />
              </button>
            )}
            <button className="sms-sb-btn sms-sb-close-btn" onClick={handleToggle} title="Close">
              <i className="fas fa-times" />
            </button>
          </div>
        </div>

        <div className="sms-sb-body">
          {messages.length === 0 ? (
            <div className="sms-sb-empty">
              <i className="fas fa-inbox" />
              <p>All clear</p>
              <small>New replies will appear here</small>
            </div>
          ) : (
            <div className="sms-sb-list">
              {messages.map(msg => {
                const name = msg.ClientName || msg.PhoneNumber || 'Unknown';
                const body = decodeMsg(msg.Message);
                const isNew = newIds.has(msg.ID);
                return (
                  <div
                    key={msg.ID}
                    className={`sms-sb-item${isNew ? ' is-new' : ''}`}
                    onClick={() => openThread(msg)}
                    title="Open conversation"
                  >
                    {isNew && <span className="sms-sb-new-dot" />}
                    <div className="sms-sb-avatar">{initials(name)}</div>
                    <div className="sms-sb-item-content">
                      <div className="sms-sb-item-top">
                        <span className="sms-sb-name">{name}</span>
                        <span className="sms-sb-time">{relTime(msg.SentDate)}</span>
                      </div>
                      <div className="sms-sb-preview">{body}</div>
                    </div>
                    <button
                      className="sms-sb-dismiss-btn"
                      onClick={(e) => dismiss(e, msg.ID)}
                      title="Dismiss"
                    >
                      <i className="fas fa-times" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="sms-sb-footer">
          {lastRefresh
            ? <small>Updated {relTime(lastRefresh)} · auto-refreshes every 60s</small>
            : <small>Loading…</small>}
        </div>
      </div>

      {isOpen && <div className="sms-sidebar-overlay" onClick={handleToggle} />}

      {threadModal && (
        <MessageThreadModal
          phoneNumber={threadModal.phoneNumber}
          customerName={threadModal.customerName}
          appointmentId={threadModal.appointmentId}
          onClose={() => setThreadModal(null)}
        />
      )}
    </>
  );
};

export default SMSResponsesSidebar;
